import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2, ShieldAlert, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1677129667171-92abd8740fa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkxMTQzNjU2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate("/");
    } catch (err) {
      const status = err?.response?.status;
      setError({ kind: status === 423 ? "locked" : status === 403 ? "device" : "error", text: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Tamanna Hotels" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/80 to-[#1E3A8A]/40" />
        <div className="absolute bottom-0 p-12 text-stone-100">
          <h2 className="font-serif text-5xl font-semibold leading-tight">Corporate Sales,<br />refined.</h2>
          <p className="mt-4 max-w-md text-stone-300 text-sm leading-relaxed">
            A unified command centre for managing corporate accounts, tariffs and approvals across all six Tamanna Hotels properties.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <div className="font-serif text-2xl font-semibold leading-none">Tamanna Hotels</div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary/80 mt-1">Sales Portal</div>
            </div>
          </div>

          <h1 className="text-3xl font-serif font-semibold">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Access your corporate sales workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            {error && (
              <div
                data-testid={`login-${error.kind}-message`}
                className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
                  error.kind === "device" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-rose-200 bg-rose-50 text-rose-800"
                }`}
              >
                {error.kind === "device" ? <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> : error.kind === "locked" ? <Lock className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                <span>{error.text}</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@tamannahotels.com" required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
